import { useCallback, useEffect, useRef, useState } from 'react';

// Cross-tab channel driving the counter-facing "Customer Display" screen from
// the operator's Booking tabs. Same-origin only, no backend involved.
export const CUSTOMER_DISPLAY_CHANNEL_NAME = 'lune_customer_display';

export const CUSTOMER_DISPLAY_ROUTE = '/admin/booking/customer-display';

// Hint to window.open() (often dragged/maximized after) and the preview's
// fallback size before the display reports its real one.
export const DEFAULT_VIEWPORT_WIDTH = 1280;
export const DEFAULT_VIEWPORT_HEIGHT = 800;

// Cross-booking guard for 'booking-code'/'booking-detail-summary': messages
// carry no session id, so an unrelated tab must not hijack this screen.
// Dismissed bookings are handled separately (see dismissedCodesRef below).
function shouldIgnoreIncomingBooking(prev, incomingCode) {
  const busyWithLiveSeating = prev.type === 'seating' && prev.data?.desktopMap;
  const busyWithDifferentBooking =
    prev.type === 'confirmation' &&
    prev.data?.bookingCode &&
    prev.data.bookingCode !== incomingCode;
  return busyWithLiveSeating || busyWithDifferentBooking;
}

export function openCustomerDisplayWindow() {
  window.open(
    `#${CUSTOMER_DISPLAY_ROUTE}`,
    'lune_customer_display_window',
    `width=${DEFAULT_VIEWPORT_WIDTH},height=${DEFAULT_VIEWPORT_HEIGHT}`
  );
}

// Shared reducer behind the real display and its operator-side preview, so
// both stay in sync. `onScrollSync` is a ref, not a dep, to avoid reopening
// the channel every render.
export function useCustomerDisplayState({ onScrollSync, onSyncRequested } = {}) {
  const [screen, setScreen] = useState({ type: 'idle' });
  const [viewportSize, setViewportSize] = useState({
    width: DEFAULT_VIEWPORT_WIDTH,
    height: DEFAULT_VIEWPORT_HEIGHT,
  });
  // Only the preview reads this, to stay hidden until there's something real
  // to mirror.
  const [isDisplayConnected, setIsDisplayConnected] = useState(false);
  // "Curtain": forces the real display back to Welcome without touching the
  // booking flow underneath. Lives here (not component state) so it
  // round-trips over the channel and survives the preview remounting on
  // every page navigation.
  const [isCurtainClosed, setIsCurtainClosedState] = useState(false);
  const curtainClosedRef = useRef(false);
  const channelRef = useRef(null);
  const onScrollSyncRef = useRef(onScrollSync);
  onScrollSyncRef.current = onScrollSync;
  const onSyncRequestedRef = useRef(onSyncRequested);
  onSyncRequestedRef.current = onSyncRequested;
  // Kept in sync with `screen` synchronously (not via useEffect, which would
  // lag a render behind) so the 'request-sync' reply below — read from a
  // channel.onmessage closure created once — never answers with a stale value.
  const screenRef = useRef(screen);
  // Bookings already resolved (paid/cancelled) and sent back to Welcome by
  // goIdle(). BookingDetail.js re-broadcasts on every edit, so without this
  // the finished ticket would pop back up. In-memory only: the display tab
  // lives for the whole shift, and a freshly remounted preview (empty set) is
  // corrected by the display's 'sync-screen' reply — see isDismissedBooking.
  const dismissedCodesRef = useRef(new Set());

  // Writes screenRef before setScreen so it's correct immediately. Hoisted as
  // a stable useCallback so goIdle can reuse it below.
  const updateScreen = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(screenRef.current) : updater;
    screenRef.current = next;
    setScreen(next);
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL_NAME);
    channelRef.current = channel;
    // The real display (the only one with onSyncRequested) also re-sends its
    // screen, so a preview that didn't know the code was dismissed and
    // already applied the message snaps back in line.
    const isDismissedBooking = (code) => {
      if (!code || !dismissedCodesRef.current.has(code)) return false;
      if (onSyncRequestedRef.current) {
        channel.postMessage({ type: 'sync-screen', screen: screenRef.current });
      }
      return true;
    };
    channel.onmessage = (event) => {
      if (event.data.type === 'request-sync') {
        // Preview asks to catch up on mount in case it missed the display's
        // mount-time broadcasts (BroadcastChannel doesn't replay to late
        // joiners). Only the real display answers, so other previews don't
        // race to reply with their own stale screen.
        if (onSyncRequestedRef.current) {
          onSyncRequestedRef.current((message) => channel.postMessage(message));
          channel.postMessage({ type: 'sync-screen', screen: screenRef.current });
          channel.postMessage({ type: 'curtain-status', closed: curtainClosedRef.current });
        }
        return;
      }
      if (event.data.type === 'sync-screen') {
        updateScreen(event.data.screen);
        return;
      }
      if (event.data.type === 'curtain-status') {
        // Broadcast by setCurtainClosed() below, or replayed here for a
        // freshly (re)mounted preview that missed the original toggle.
        curtainClosedRef.current = event.data.closed;
        setIsCurtainClosedState(event.data.closed);
        return;
      }
      if (event.data.type === 'viewport-size') {
        // Lets the preview render at the display's real width so their
        // scroll ratios stay in sync. Only the real display sends this, so
        // receiving one also proves it's live.
        setViewportSize({ width: event.data.width, height: event.data.height });
        setIsDisplayConnected(true);
        return;
      }
      if (event.data.type === 'display-offline') {
        // Sent right before the display's window/tab closes, so the preview
        // hides itself instead of showing a stale mirror.
        setIsDisplayConnected(false);
        return;
      }
      if (event.data.type === 'scroll-sync') {
        // From the preview as the operator scrolls it — the real display is
        // only ever scrolled remotely, never directly.
        onScrollSyncRef.current?.(event.data.ratio);
        return;
      }
      if (event.data.type === 'booking-code') {
        // Sent from BookingPayment.js (Proceed) and BookingDetail.js (mount
        // / reload) once a booking code exists. Cross-booking guard: see
        // shouldIgnoreIncomingBooking above.
        if (isDismissedBooking(event.data.code)) return;
        updateScreen((prev) => {
          if (shouldIgnoreIncomingBooking(prev, event.data.code)) {
            return prev;
          }
          // A code means the booking already exists, so the seat hold is
          // moot — drop expireAt too and let the countdown disappear.
          return {
            type: 'confirmation',
            data: { ...prev.data, bookingCode: event.data.code, expireAt: null },
          };
        });
        return;
      }
      if (event.data.type === 'booking-detail-summary') {
        // Full picture from BookingDetail.js (mount / data changes), so the
        // display is populated even when this page is opened directly. Same
        // guard as booking-code above.
        if (isDismissedBooking(event.data.data?.bookingCode)) return;
        updateScreen((prev) => {
          if (shouldIgnoreIncomingBooking(prev, event.data.data?.bookingCode)) {
            return prev;
          }
          return {
            type: 'confirmation',
            data: { ...prev.data, ...event.data.data },
          };
        });
        return;
      }
      if (event.data.type === 'qr-payment') {
        // Mirrors BookingDetail.js's VietQR modal so the guest can scan it
        // from the counter screen. `data` is null once resolved/closed.
        updateScreen((prev) => ({
          ...prev,
          data: { ...prev.data, qrPayment: event.data.data },
        }));
        return;
      }
      if (event.data.type === 'payment-summary') {
        // Real rate-plan-aware per-seat amounts, replacing the zone-based estimate.
        updateScreen((prev) => ({
          ...prev,
          data: { ...prev.data, paymentBreakdown: event.data.data },
        }));
        return;
      }
      if (event.data.type === 'idle') {
        // Reset signal (cancel/expire/paid). Only applies if tagged for the
        // session/booking currently on screen (by expireAt or code).
        // Code-tagged idles only come from goIdle(), i.e. a resolved booking
        // — remember it here too so previews ignore its later updates.
        if (event.data.code) dismissedCodesRef.current.add(event.data.code);
        updateScreen((prev) => {
          if (prev.type === 'idle') return prev;
          const sameSessionByExpiry =
            event.data.expireAt && prev.data?.expireAt === event.data.expireAt;
          const sameBookingByCode =
            event.data.code && prev.data?.bookingCode === event.data.code;
          if (sameSessionByExpiry || sameBookingByCode) {
            return { type: 'idle' };
          }
          return prev;
        });
        return;
      }
      updateScreen(event.data);
    };
    // A reloaded display starts with the curtain open — resync mounted previews.
    if (onSyncRequestedRef.current) {
      channel.postMessage({ type: 'sync-screen', screen: screenRef.current });
      channel.postMessage({ type: 'curtain-status', closed: curtainClosedRef.current });
    }
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, []);

  const sendMessage = useCallback((message) => {
    channelRef.current?.postMessage(message);
  }, []);

  // A tab never receives its own broadcasts, so update this tab's screen
  // directly; the postMessage is only for other mirrored previews.
  const goIdle = useCallback((code) => {
    if (code) dismissedCodesRef.current.add(code);
    updateScreen({ type: 'idle' });
    channelRef.current?.postMessage({ type: 'idle', code });
  }, [updateScreen]);

  // Same self-delivery gap as goIdle: update locally and broadcast.
  const setCurtainClosed = useCallback((closed) => {
    curtainClosedRef.current = closed;
    setIsCurtainClosedState(closed);
    channelRef.current?.postMessage({ type: 'curtain-status', closed });
  }, []);

  return {
    screen,
    sendMessage,
    viewportSize,
    isDisplayConnected,
    goIdle,
    isCurtainClosed,
    setCurtainClosed,
  };
}
